import { Link, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { useRequest } from "../hooks/useRequest";
import { useAuth } from "../context/AuthContext";
import { useLeaguePublicContext } from "./LeaguePublicLayout";
import { getSeasonStatsUrl } from "../config/api";
import { TeamStat } from "../types/models";
import TeamDisplay from "../Common/TeamDisplay";

interface StandingsTableProps {
  standings: TeamStat[];
  title: string;
  abbr: string;
}

function StandingsTable({ standings, title, abbr }: StandingsTableProps) {
  return (
    <div className="lp-card" style={{ marginBottom: "16px" }}>
      <div className="lp-roster-header">
        <strong style={{ fontSize: "13px" }}>{title}</strong>
      </div>
      {standings.length === 0 ? (
        <div className="lp-card-empty">No standings yet.</div>
      ) : (
        <table className="lp-table" aria-label={`${title} standings`}>
          <thead>
            <tr>
              <th className="lp-th-left" style={{ width: "18px" }}></th>
              <th className="lp-th-left">Team</th>
              <th title="Games played">GP</th>
              <th title="Wins">W</th>
              <th title="Losses">L</th>
              <th title="Ties">T</th>
              <th title="Goals for">GF</th>
              <th title="Goals against">GA</th>
              <th className="lp-th-mobile-hide" title="Goal differential">
                DIFF
              </th>
              <th className="lp-th-pts" title="Points">
                PTS
              </th>
            </tr>
          </thead>
          <tbody>
            {standings.map((row, i) => (
              <tr key={row.team_id}>
                <td className="lp-td-left lp-rank">{i + 1}</td>
                <td className="lp-td-left">
                  <Link
                    to={`/leagues/${abbr}/teams/${row.team_id}`}
                    className="lp-team-link"
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <TeamDisplay
                      team={{
                        id: row.team_id,
                        name: row.team_name,
                        colour_code: row.colour_code,
                      }}
                    />
                  </Link>
                </td>
                <td style={{ color: "var(--text-secondary)" }}>
                  {row.games_played}
                </td>
                <td>{row.wins}</td>
                <td>{row.losses}</td>
                <td>{row.ties}</td>
                <td>{row.goals_for}</td>
                <td>{row.goals_against}</td>
                <td
                  className="lp-td-mobile-hide lp-goal-diff"
                  data-diff={
                    row.goals_for - row.goals_against > 0
                      ? "positive"
                      : row.goals_for - row.goals_against < 0
                        ? "negative"
                        : "neutral"
                  }
                >
                  {row.goals_for - row.goals_against > 0 ? "+" : ""}
                  {row.goals_for - row.goals_against}
                </td>
                <td className="lp-td-pts lp-pts">{row.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StandingsPage() {
  const auth = useAuth();
  const { abbr } = useParams<{ abbr: string }>();
  const { seasonId, gameTypeFilter } = useLeaguePublicContext();

  // Fetch playoff stats
  const playoffStatsUrl = seasonId
    ? getSeasonStatsUrl(seasonId, "playoff")
    : undefined;
  const { data: playoffStatsResponse } = useRequest<TeamStat[]>(
    playoffStatsUrl,
    auth.token,
  );

  // Fetch regular season stats
  const regularStatsUrl = seasonId
    ? getSeasonStatsUrl(seasonId, "regular")
    : undefined;
  const { data: regularStatsResponse } = useRequest<TeamStat[]>(
    regularStatsUrl,
    auth.token,
  );

  const playoffStandings = [...(playoffStatsResponse ?? [])].sort((a, b) => {
    const ptsDiff = b.points - a.points;
    if (ptsDiff !== 0) return ptsDiff;
    return b.goals_for - b.goals_against - (a.goals_for - a.goals_against);
  });

  const regularStandings = [...(regularStatsResponse ?? [])].sort((a, b) => {
    const ptsDiff = b.points - a.points;
    if (ptsDiff !== 0) return ptsDiff;
    return b.goals_for - b.goals_against - (a.goals_for - a.goals_against);
  });

  if (!seasonId) {
    return (
      <div className="lp-loading">
        <Spinner animation="border" variant="secondary" role="status" />
      </div>
    );
  }

  if (!playoffStatsResponse || !regularStatsResponse) {
    return (
      <div className="lp-loading">
        <Spinner animation="border" variant="secondary" role="status" />
      </div>
    );
  }

  // Check if there are any playoff games played
  const hasPlayoffGames = playoffStandings.some(
    (stat) => stat.games_played > 0,
  );

  // Determine which tables to show based on filter
  const showPlayoffs =
    (gameTypeFilter === "all" || gameTypeFilter === "playoff") &&
    hasPlayoffGames;
  const showRegular = gameTypeFilter === "all" || gameTypeFilter === "regular";

  return (
    <div>
      {showPlayoffs && (
        <StandingsTable
          standings={playoffStandings}
          title="Playoff Standings"
          abbr={abbr!}
        />
      )}
      {showRegular && (
        <StandingsTable
          standings={regularStandings}
          title="Regular Season Standings"
          abbr={abbr!}
        />
      )}
    </div>
  );
}

export default StandingsPage;
